import { useEffect, useRef, useState } from 'react';

const STATIC_CAT = '/media/tuition/cat-static.webp';
const CLICK_CAT = '/media/tuition/cat-click.webp';
const HORIZONTAL_ATLAS = '/media/tuition/cat-look-horizontal.webp';
const VERTICAL_ATLAS = '/media/tuition/cat-look-vertical.webp';
const DIAGONAL_ATLAS = '/media/tuition/cat-look-diagonal.webp';

const TILE = 512;
const HORIZONTAL_COLUMNS = 6;
const HORIZONTAL_COUNT = 33;
const HORIZONTAL_CENTRE = 16;
const VERTICAL_COLUMNS = 5;
const VERTICAL_COUNT = 17;
const VERTICAL_CENTRE = 8;
const DIAGONAL_COLUMNS = 3;
const DIAGONAL_COUNT = 5;
const FRONT_FRAME = 4;
const LOOK_RESET_MS = 4000;
const TOUCH_MOVE_TOLERANCE = 12;
const ANIM_SCALE = 'scale-[1.22]';
const ANIM_MS = 6300;
const INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'summary',
  'h1',
  'h2',
  'h3',
  'p',
  '[role="button"]',
  '[contenteditable="true"]',
  '[data-no-mascot-look]',
].join(',');

type LookAxis = 'horizontal' | 'vertical' | 'diagonal';

interface ActiveTouch {
  pointerId: number;
  startX: number;
  startY: number;
  latestX: number;
  latestY: number;
  moved: boolean;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export default function TuitionMascot() {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reactionTimeoutRef = useRef<number | null>(null);
  const lookResetTimeoutRef = useRef<number | null>(null);
  const activeTouchRef = useRef<ActiveTouch | null>(null);
  const isPlayingRef = useRef(false);
  const resetLookRef = useRef<() => void>(() => {});
  const [isPlaying, setIsPlaying] = useState(false);
  const [playToken, setPlayToken] = useState(0);
  const [spriteReady, setSpriteReady] = useState(false);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    const button = buttonRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!button || !canvas || !context) return;

    const horizontalAtlas = new Image();
    const verticalAtlas = new Image();
    const diagonalAtlas = new Image();
    let disposed = false;
    let ready = false;
    let animationFrame = 0;
    let previousTime = 0;
    let axis: LookAxis = 'diagonal';
    let targetAxis: LookAxis = 'diagonal';
    let currentFrame = FRONT_FRAME;
    let targetFrame = FRONT_FRAME;
    let drawnAxis: LookAxis | null = null;
    let drawnFrame = -1;

    const axisDetails = (selectedAxis: LookAxis) => {
      if (selectedAxis === 'horizontal') return {
          atlas: horizontalAtlas,
          columns: HORIZONTAL_COLUMNS,
          count: HORIZONTAL_COUNT,
          centre: HORIZONTAL_CENTRE,
        };
      if (selectedAxis === 'vertical') return {
          atlas: verticalAtlas,
          columns: VERTICAL_COLUMNS,
          count: VERTICAL_COUNT,
          centre: VERTICAL_CENTRE,
        };
      return {
        atlas: diagonalAtlas,
        columns: DIAGONAL_COLUMNS,
        count: DIAGONAL_COUNT,
        centre: FRONT_FRAME,
      };
    };

    const drawPose = (
      targetContext: CanvasRenderingContext2D,
      selectedAxis: LookAxis,
      selectedFrame: number,
    ) => {
      const details = axisDetails(selectedAxis);
      const frame = clamp(Math.round(selectedFrame), 0, details.count - 1);
      targetContext.drawImage(
        details.atlas,
        (frame % details.columns) * TILE,
        Math.floor(frame / details.columns) * TILE,
        TILE,
        TILE,
        0,
        0,
        TILE,
        TILE,
      );
      return frame;
    };

    const draw = () => {
      if (!ready) return;
      const details = axisDetails(axis);
      const frame = clamp(Math.round(currentFrame), 0, details.count - 1);
      if (drawnAxis === axis && drawnFrame === frame) return;

      context.clearRect(0, 0, TILE, TILE);
      drawPose(context, axis, currentFrame);
      drawnAxis = axis;
      drawnFrame = frame;
    };

    const tick = (time: number) => {
      animationFrame = 0;
      if (!ready || document.hidden) return;

      if (axis !== targetAxis) {
        axis = targetAxis;
        currentFrame = axis === 'diagonal' ? targetFrame : axisDetails(axis).centre;
        drawnAxis = null;
      }

      if (axis === 'diagonal') {
        currentFrame = targetFrame;
        draw();
        return;
      }

      const elapsed = previousTime ? Math.min(time - previousTime, 50) : 16;
      previousTime = time;
      const step = (targetFrame - currentFrame) * (1 - Math.exp(-elapsed / 80));
      currentFrame += clamp(step, -elapsed / 20, elapsed / 20);
      if (Math.abs(targetFrame - currentFrame) < 0.04) currentFrame = targetFrame;
      draw();

      if (currentFrame !== targetFrame) {
        animationFrame = requestAnimationFrame(tick);
      }
    };

    const startAnimation = () => {
      if (!ready || document.hidden || animationFrame) return;
      previousTime = 0;
      animationFrame = requestAnimationFrame(tick);
    };

    const cancelLookResetTimer = () => {
      if (lookResetTimeoutRef.current !== null) {
        window.clearTimeout(lookResetTimeoutRef.current);
        lookResetTimeoutRef.current = null;
      }
    };

    const scheduleLookReset = () => {
      cancelLookResetTimer();
      lookResetTimeoutRef.current = window.setTimeout(() => {
        lookResetTimeoutRef.current = null;
        resetLook();
      }, LOOK_RESET_MS);
    };

    const resetLook = () => {
      cancelLookResetTimer();
      targetAxis = 'diagonal';
      targetFrame = FRONT_FRAME;
      startAnimation();
    };
    resetLookRef.current = resetLook;

    const lookAtPoint = (clientX: number, clientY: number, holdLook = false) => {
      if (!ready || isPlayingRef.current) return;
      const bounds = button.getBoundingClientRect();
      const eyeX = bounds.left + bounds.width * 0.5;
      const eyeY = bounds.top + bounds.height * 0.36;
      const isOverCat = clientX >= bounds.left
        && clientX <= bounds.right
        && clientY >= bounds.top
        && clientY <= bounds.bottom;
      if (isOverCat) {
        resetLook();
        return;
      }
      const horizontalReach = clientX < eyeX
        ? Math.max(eyeX, 1)
        : Math.max(window.innerWidth - eyeX, 1);
      const verticalReach = clientY < eyeY
        ? Math.max(eyeY, 1)
        : Math.max(window.innerHeight - eyeY, 1);
      const horizontalAmount = clamp(
        (clientX - eyeX) / horizontalReach,
        -1,
        1,
      );
      const verticalAmount = clamp(
        (clientY - eyeY) / verticalReach,
        -1,
        1,
      );

      const horizontalMagnitude = Math.abs(horizontalAmount);
      const verticalMagnitude = Math.abs(verticalAmount);
      const smallerMagnitude = Math.min(horizontalMagnitude, verticalMagnitude);
      const largerMagnitude = Math.max(horizontalMagnitude, verticalMagnitude);
      const isDiagonal = smallerMagnitude > 0.12
        && smallerMagnitude / Math.max(largerMagnitude, 0.001) > 0.34;

      if (Math.hypot(horizontalAmount, verticalAmount) < 0.1) {
        resetLook();
        return;
      }

      if (isDiagonal) {
        targetAxis = 'diagonal';
        // Atlas order is explicitly TL, TR, BL, BR in screen coordinates.
        targetFrame = (verticalAmount > 0 ? 2 : 0) + (horizontalAmount > 0 ? 1 : 0);
      } else if (verticalMagnitude > horizontalMagnitude * 1.15) {
        targetAxis = 'vertical';
        targetFrame = VERTICAL_CENTRE - verticalAmount * VERTICAL_CENTRE;
      } else {
        targetAxis = 'horizontal';
        // Positive screen X (cursor right of eyes) should advance toward
        // frames where the cat looks right.
        targetFrame = HORIZONTAL_CENTRE + horizontalAmount * HORIZONTAL_CENTRE;
      }
      startAnimation();
      if (holdLook) scheduleLookReset();
      else cancelLookResetTimer();
    };

    const clearActiveTouch = (pointerId?: number) => {
      const touch = activeTouchRef.current;
      if (!touch || (pointerId !== undefined && touch.pointerId !== pointerId)) return null;
      activeTouchRef.current = null;
      return touch;
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch' || !event.isPrimary || isPlayingRef.current) return;
      const target = event.target;
      if (target instanceof Element && target.closest(INTERACTIVE_SELECTOR)) return;

      activeTouchRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        latestX: event.clientX,
        latestY: event.clientY,
        moved: false,
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') {
        lookAtPoint(event.clientX, event.clientY);
        return;
      }

      const touch = activeTouchRef.current;
      if (!touch || touch.pointerId !== event.pointerId || touch.moved) return;
      touch.latestX = event.clientX;
      touch.latestY = event.clientY;
      const distance = Math.hypot(event.clientX - touch.startX, event.clientY - touch.startY);
      if (distance > TOUCH_MOVE_TOLERANCE) touch.moved = true;
    };

    const handlePointerUp = (event: PointerEvent) => {
      const touch = clearActiveTouch(event.pointerId);
      if (!touch || touch.moved || isPlayingRef.current) return;
      lookAtPoint(touch.latestX, touch.latestY, true);
      navigator.vibrate?.(8);
    };

    const handlePointerCancel = (event: PointerEvent) => {
      clearActiveTouch(event.pointerId);
    };

    const handleVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(animationFrame);
        animationFrame = 0;
        activeTouchRef.current = null;
        resetLook();
      }
    };

    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    window.addEventListener('blur', resetLook);
    document.documentElement.addEventListener('pointerleave', resetLook);
    document.addEventListener('visibilitychange', handleVisibility);

    void Promise.all([
      new Promise<void>((resolve, reject) => {
        horizontalAtlas.onload = () => resolve();
        horizontalAtlas.onerror = () => reject();
        horizontalAtlas.src = HORIZONTAL_ATLAS;
      }),
      new Promise<void>((resolve, reject) => {
        verticalAtlas.onload = () => resolve();
        verticalAtlas.onerror = () => reject();
        verticalAtlas.src = VERTICAL_ATLAS;
      }),
      new Promise<void>((resolve, reject) => {
        diagonalAtlas.onload = () => resolve();
        diagonalAtlas.onerror = () => reject();
        diagonalAtlas.src = DIAGONAL_ATLAS;
      }),
    ]).then(() => {
      if (disposed) return;
      ready = true;
      draw();
      setSpriteReady(true);
    }).catch(() => {
      if (!disposed) setSpriteReady(false);
    });

    return () => {
      disposed = true;
      cancelLookResetTimer();
      cancelAnimationFrame(animationFrame);
      resetLookRef.current = () => {};
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      window.removeEventListener('blur', resetLook);
      document.documentElement.removeEventListener('pointerleave', resetLook);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => () => {
    if (reactionTimeoutRef.current !== null) window.clearTimeout(reactionTimeoutRef.current);
    if (lookResetTimeoutRef.current !== null) window.clearTimeout(lookResetTimeoutRef.current);
  }, []);

  const resetToStill = () => {
    if (reactionTimeoutRef.current !== null) {
      window.clearTimeout(reactionTimeoutRef.current);
      reactionTimeoutRef.current = null;
    }
    setIsPlaying(false);
  };

  const playReaction = () => {
    if (reactionTimeoutRef.current !== null) {
      window.clearTimeout(reactionTimeoutRef.current);
      reactionTimeoutRef.current = null;
    }

    resetLookRef.current();
    setPlayToken((token) => token + 1);
    setIsPlaying(true);
    reactionTimeoutRef.current = window.setTimeout(resetToStill, ANIM_MS);
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={playReaction}
      aria-label={isPlaying ? 'Replay the tuition cat animation' : 'Play the tuition cat animation'}
      className="group relative mx-auto block h-40 w-40 bg-transparent transition-transform duration-300 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-4 sm:h-48 sm:w-48"
    >
      <img
        src={STATIC_CAT}
        alt=""
        width={TILE}
        height={TILE}
        draggable={false}
        className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-150 ${isPlaying || spriteReady ? 'opacity-0' : 'opacity-100'}`}
      />
      <canvas
        ref={canvasRef}
        width={TILE}
        height={TILE}
        aria-hidden="true"
        className={`absolute inset-0 h-full w-full transition-opacity duration-150 ${spriteReady && !isPlaying ? 'opacity-100' : 'opacity-0'}`}
      />
      {isPlaying && (
        <img
          key={playToken}
          src={`${CLICK_CAT}?v=${playToken}`}
          alt=""
          draggable={false}
          className={`absolute inset-0 h-full w-full object-contain ${ANIM_SCALE}`}
        />
      )}
    </button>
  );
}
